// src/pages/OverviewFTE.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Dashboard.css";
import "../styles/buttons.css";
import "./ManageRequest.css"; // reuse the same modal layout/styles as ManageRequest
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ENV, tbGetJson, tbPostJson } from "../api/client";
import { isLookupReady } from "../hooks/useLookupReady";

/* -------------------- history patch (unchanged) -------------------- */
(function patchHistoryOnce() {
    if (typeof window === "undefined" || window.__npw_history_patched__) return;
    try {
        const { pushState, replaceState } = window.history;
        window.history.pushState = function (...args) {
            const r = pushState.apply(this, args);
            window.dispatchEvent(new Event("locationchange"));
            return r;
        };
        window.history.replaceState = function (...args) {
            const r = replaceState.apply(this, args);
            window.dispatchEvent(new Event("locationchange"));
            return r;
        };
        window.addEventListener("popstate", () => window.dispatchEvent(new Event("locationchange")));
        window.__npw_history_patched__ = true;
    } catch {
        /* no-op */
    }
})();

/* Debounced page activation: ONLY locationchange & pageshow (no focus/visibility) */
function usePageActivation(fn, debounceMs = 200) {
    const tRef = useRef(null);
    useEffect(() => {
        const kick = () => {
            if (tRef.current) clearTimeout(tRef.current);
            tRef.current = setTimeout(() => fn?.(), debounceMs);
        };
        window.addEventListener("pageshow", kick);
        window.addEventListener("locationchange", kick);
        return () => {
            window.removeEventListener("pageshow", kick);
            window.removeEventListener("locationchange", kick);
            if (tRef.current) clearTimeout(tRef.current);
        };
    }, [fn, debounceMs]);
}

/* ===== Helpers ===== */
const clean = (s) => (s == null ? "" : String(s)).trim();
const getStoredRole = () => clean(sessionStorage.getItem("tb_role")) || "FTE Nominator";
const getEmail = () => clean(sessionStorage.getItem("tb_email"));
const getActorName = () => clean(sessionStorage.getItem("tb_name"));
const getActorEmail = () =>
    clean(sessionStorage.getItem("tb_email")) ||
    clean(sessionStorage.getItem("email")) ||
    "";

const getDtvId = () => clean(sessionStorage.getItem("tb_dtvid"));
const getName = () => clean(sessionStorage.getItem("tb_name"));

function getActorDtvId() {
    return clean(sessionStorage.getItem("tb_dtvid")) || getEmail() || "";
}

/** Submission context to avoid null submitter/role downstream */
function primeSubmitContext({ mode, self, selectedDtvid }) {
    const role = getStoredRole(); // for submit context only (not for display)
    const selfEmail = getEmail() || clean(self?.primaryEmail);
    const selfName =
        getName() || [clean(self?.firstName), clean(self?.lastName)].filter(Boolean).join(" ").trim();

    const sponsorId = getDtvId() || clean(self?.dtvAnchorId) || selfEmail;
    const workerDtvid =
        mode === "others" ? clean(selectedDtvid) : clean(self?.dtvAnchorId) || getDtvId();

    const ctx = {
        submitter: selfEmail || null,
        submitter_name: selfName || null,
        loggedInUserRole: role || null,
        nominatorRole: role || null,
        sponsorId: sponsorId || null,
        workerDtvid: workerDtvid || null,
        at: new Date().toISOString(),
        source: "OverviewFTE",
    };

    try {
        sessionStorage.setItem("npw.submit.ctx", JSON.stringify(ctx));
        sessionStorage.setItem("npw_submitter", ctx.submitter || "");
        sessionStorage.setItem("npw_submitter_name", ctx.submitter_name || "");
        sessionStorage.setItem("npw_loggedInUserRole", ctx.loggedInUserRole || "");
        sessionStorage.setItem("npw_nominatorRole", ctx.nominatorRole || "");
        if (workerDtvid) sessionStorage.setItem("tb_selected_dtvid", workerDtvid);
    } catch {
        /* ignore */
    }
}

/* ===== Notifications (env-driven) ===== */
function buildNotifUrl() {
    const base = (ENV.TB_NOTIF_URL || "").replace(/\/$/, "");
    if (!base) return "";
    const storedRole = getStoredRole();
    const roleKey = storedRole.toLowerCase();
    const email = getEmail();
    const dtvId = getDtvId();
    const vendorId = clean(sessionStorage.getItem("tb_vendorId"));
    const supplierName = clean(sessionStorage.getItem("tb_supplierName"));
    const vendor = (vendorId || supplierName || "").trim();
    const sponsorId = (dtvId || email || "").trim();

    const params = new URLSearchParams();
    params.set("loggedInUserRole", storedRole);
    params.set("role", storedRole);

    // Do NOT add vendor for FTE. Only plain "Nominator" includes vendor.
    if (roleKey === "fte nominator" || roleKey === "fte") {
        if (dtvId) params.set("id", dtvId);
        if (email) params.set("email", email);
    } else if (roleKey === "nominator") {
        if (vendor) params.set("vendor", vendor);
        if (email) params.set("id", email);
    } else if (roleKey === "employee supplier admin") {
        if (email) params.set("id", email);
    } else if (roleKey === "employee sponsor") {
        if (sponsorId) params.set("sponsor", sponsorId);
    } else if (roleKey === "cp channel delegate") {
        if (email) params.set("id", email);
    } else {
        const id = email || dtvId;
        if (id) params.set("id", id);
    }
    return `${base}?${params.toString()}`;
}

const NOTIF_FALLBACK = [
    { id: "N1", title: "Pending approval: Darshan Nov 05 Kumar", ago: "1 hr ago" },
    { id: "N2", title: "Approved: Testing Sprint5", ago: "29 days ago" },
    { id: "N3", title: "Pending approval: Reddy Thallapalli", ago: "29 days ago" },
];

/* ===== TB-proxied Lookup (Self + Direct Reports) =====
   IMPORTANT:
   - NO direct Hinge base usage from browser.
   - Use either:
       1) ENV.LOOKUP_URL (if already routed via TB), else
       2) ENV.TB_HINGE_LOOKUP_URL (new TB proxy endpoint)
*/
function resolveLookupBase() {
    const a = clean(ENV.LOOKUP_URL || "");
    if (a) return a.replace(/\/$/, "");
    // const b0 = clean(ENV.TB_HINGE_PERSON_LOOKUP_URL || "");
    // if (b0) return b0.replace(/\/$/, "");
    const b = clean(ENV.TB_HINGE_LOOKUP_URL || "");
    if (b) return b.replace(/\/$/, "");
    // const c = clean(ENV.TB_LOOKUP_URL || "");
    // if (c) return c.replace(/\/$/, "");
    return "";
}

function buildLookupUrl() {
    const base = resolveLookupBase();
    if (!base) return "";

    const anchor = getDtvId();
    const email = getEmail();

    // Keep the same query contract as earlier: dtvId preferred, else email, else DEFAULT_DTVID

    const u = new URL(base, typeof window !== "undefined" ? window.location.origin : "http://localhost");
    if (anchor) u.searchParams.set("dtvId", anchor);
    else if (email) u.searchParams.set("email", email);
    else u.searchParams.set("dtvId", clean(ENV.DEFAULT_DTVID));
    return u.toString();
}

function unwrapLookup(payload) {
    if (!payload || typeof payload !== "object") return null;
    if (payload.response && typeof payload.response === "object") return payload.response;
    return payload;
}

function isPlaceholderSelf(self) {
    if (!self || typeof self !== "object") return true;
    const dtv = clean(self?.dtvAnchorId);
    const email = clean(self?.primaryEmail);
    const first = clean(self?.firstName);
    const last = clean(self?.lastName);
    const allBlank = !dtv && !email && !first && !last;
    const allDash = [dtv, email, first, last].filter(Boolean).every((v) => v === "-");
    return allBlank || allDash;
}

function mapDirectReport(dr) {
    const dtvid =
        clean(dr?.dtvId) ||
        clean(dr?.dtvid) ||
        clean(dr?.dtvID) ||
        clean(dr?.dtv_id) ||
        clean(dr?.dtvAnchorId) ||
        "";

    const attuid = clean(dr?.attuid) || "";
    const first = clean(dr?.firstName) || "";
    const last = clean(dr?.lastName) || "";
    const role = clean(dr?.role) || clean(dr?.dtvRoleName) || "-";
    const status = clean(dr?.personStatus) || "Active";
    const start_date = clean(dr?.startDate) || "-";
    const end_date = dr?.endDate == null || clean(dr?.endDate) === "" ? "-" : clean(dr?.endDate);

    return {
        dtvid,
        attuid,
        deactivatedAttuid: clean(dr?.deactivatedAttuid) || "",
        first_name: first,
        last_name: last,
        role,
        status,
        start_date,
        end_date,
        email: clean(dr?.email) || "",
    };
}

/* Remove any "vendor: ..." in rendered notification titles */
function formatNotifTitle(n) {
    const raw = n?.title ?? n?.message ?? n?.text ?? n?.subject ?? "";
    if (!raw) return "";
    return String(raw)
        .replace(/\s*\(\s*vendor\s*:\s*null\s*\)/gi, "")
        .replace(/\s*,?\s*vendor\s*:\s*null/gi, "")
        .replace(/\s*\(\s*from\s*:\s*(?:null|undefined)\s*\)/gi, "")
        .replace(/\s*,?\s*from\s*:\s*(?:null|undefined)/gi, "")
        .replace(/\bfrom\b\s*(?:null|undefined)\b/gi, "")
        .replace(/\bfrom\b\s*$/gi, "")
        .replace(/\s{2,}/g, " ")
        .replace(/[,\s]+$/g, "")
        .trim();
}

/* ===== Offboard ===== */
const OFFBOARD_URL = ENV.OFFBOARD_URL;

function safeJsonParse(maybeJson) {
    if (maybeJson == null) return null;
    if (typeof maybeJson === "object") return maybeJson;
    const s = String(maybeJson || "").trim();
    if (!s) return null;
    try {
        return JSON.parse(s);
    } catch {
        return null;
    }
}

function extractOffboardError(err) {
    const body = err?.body ?? err?.response?.body ?? null;
    const parsed = safeJsonParse(body) || safeJsonParse(err?.response) || safeJsonParse(err?.data) || null;

    const payload = parsed && typeof parsed === "object" ? parsed : null;
    const inner = payload?.error && typeof payload.error === "object" ? payload.error : null;

    const code = inner?.error || payload?.code || payload?.errorCode || err?.code || err?.name || "";
    const message =
        inner?.message ||
        payload?.message ||
        (typeof payload?.error === "string" ? payload.error : "") ||
        err?.message ||
        "Offboard failed.";

    const correlationId = payload?.correlationId || payload?.correlationID || payload?.corrId || "";

    const base = code && !String(message).toLowerCase().includes(String(code).toLowerCase())
        ? `${code}: ${message}`
        : String(message);

    return correlationId ? `${base} (CorrelationId: ${correlationId})` : base;
}

/**
 * Hard-force PUT for offboarding (do not rely on tbPostJson overrides).
 * This guarantees:
 * - method=PUT
 * - JSON body
 * - Content-Type header
 * - readable error message surface
 */
async function tbPutJson(url, bodyObj, { headers = {}, fetchInit = {} } = {}) {

    if (!url) throw new Error("URL is required");
     
    // const h = {
    //     "Content-Type": "application/json",
    //     ...headers,
    // };

    // const actor = clean(sessionStorage.getItem("tb_dtvid")) || clean(sessionStorage.getItem("tb_email")) || "";
    // if (actor && !h["x-actor"] && !h["X-Actor"]) h["x-actor"] = actor;

    // const resp = await fetch(url, {
    //   //  method: "PUT",
    //     method: "POST", // TB proxy only supports POST, it will internally route to PUT based on payload.action
    //     headers: h,
    //     body: JSON.stringify(bodyObj ?? {}),
    //     ...fetchInit,
    // });

    // const text = await resp.text().catch(() => "");
    // let parsed = null;
    // try {
    //     parsed = text ? JSON.parse(text) : null;
    // } catch {
    //     parsed = null;
    // }

    // if (!resp.ok) {
    //     const msg =
    //         (parsed && (parsed.message || parsed.error?.message || parsed.error || parsed.msg)) ||
    //         resp.statusText ||
    //         "Request failed";
    //     throw new Error(`${msg} (HTTP ${resp.status})`);
    // }

    // return parsed ?? (text ? text : {});

    // Match ManageRequest offboard behavior:
    // - Content-Type: application/json
    // - credentials: include
    // - TB auth headers / cookie-session recovery handled by tbPostJson
    // - TB proxy expects POST (routes internally based on payload.action)
    const safeFetchInit = { ...(fetchInit || {}) };
    // Prevent clobbering tbPostJson's JSON headers.
    if (safeFetchInit.headers) delete safeFetchInit.headers;

    return await tbPostJson(url, bodyObj ?? {}, {
        headers: headers || {},
        fetchInit: { method: "POST", ...safeFetchInit },
    });
}

export default function OverviewFTE() {
    const navigate = useNavigate();

    const notifTargetPath = (n, idx) => {
        const titleText =
            n?.title ??
            n?.message ??
            n?.text ??
            n?.subject ??
            n?.notificationTitle ??
            "";

        const rawStatus =
            n?.status ??
            n?.personStatus ??
            n?.currentStatus ??
            n?.request_status ??
            n?.status_key ??
            n?.state ??
            "";
        const status = String(rawStatus).toLowerCase().trim();
        const isDraft =
            status === "draft" ||
            status.includes("draft") ||
            String(titleText).toLowerCase().includes("draft");

        const pickNpwId = () =>
            n?.npwId ||
            n?.npw_id ||
            n?.requestId ||
            n?.request_id ||
            n?.client_request_id ||
            n?.reqId ||
            // NOTE: n.id is often a notification id, not an NPW id.
            // Keep it as a last-resort fallback only.
            n?.id ||
            "";

        const inferIdFromText = () => {
            const s = String(titleText || "");
            if (!s) return "";

            const m1 = s.match(/\bNPW\s*ID\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m1?.[1]) return m1[1];
            const m2 = s.match(/\bNPWID\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m2?.[1]) return m2[1];
            const m3 = s.match(/\brequest\s*(?:id|#)?\s*[:=#]?\s*(\d{1,12})\b/i);
            if (m3?.[1]) return m3[1];
            const m4 = s.match(/\/onboarding\/new\/(\d{1,12})\b/i);
            if (m4?.[1]) return m4[1];

            return "";
        };

        const finalNpwId = pickNpwId() || inferIdFromText();
        if (!finalNpwId) return "";

        return isDraft
            ? `/onboarding/new/${encodeURIComponent(String(finalNpwId))}`
            : `/manage-requests/NPWID=${encodeURIComponent(String(finalNpwId))}`;
    };

    const [personInfo, setPersonInfo] = useState(null);
    const [allRows, setAllRows] = useState([]);
    const [total, setTotal] = useState(0);
    const PAGE_SIZE_KEY = "tb_overviewfte_pageSize";
    const getInitialPageSize = () => {
        try {
            const raw =
                sessionStorage.getItem(PAGE_SIZE_KEY) ||
                (typeof localStorage !== "undefined" ? localStorage.getItem(PAGE_SIZE_KEY) : null);
            const n = parseInt(String(raw || ""), 10);
            if ([5, 10, 25, 50].includes(n)) return n;
        } catch { }
        return 5;
    };

    const [pageSize, setPageSize] = useState(getInitialPageSize);

    useEffect(() => {
        try {
            sessionStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
            if (typeof localStorage !== "undefined") {
                localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
            }
        } catch { }
    }, [pageSize]);
    const [page, setPage] = useState(1);

    // DEFAULT must be SELF always
    const [mode, setMode] = useState("self"); // "self" \| "others"

    const [showNotifs, setShowNotifs] = useState(true);

    const leftColRef = useRef(null);
    const syncRailRef = useRef(null);
    const [railBox, setRailBox] = useState({ left: 0, width: 0 });

    useEffect(() => {
        function syncRail() {
            const el = leftColRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();

            const pad = 12;
            const viewportLeft = window.scrollX;
            const viewportRight = window.scrollX + window.innerWidth;

            let width = Math.max(0, r.width);
            width = Math.min(width, Math.max(0, window.innerWidth - pad * 2));

            let left = r.left + window.scrollX;
            left = Math.max(viewportLeft + pad, Math.min(left, viewportRight - width - pad));

            setRailBox({ left, width });
        }

        syncRailRef.current = syncRail;
        syncRail();
        window.addEventListener("resize", syncRail);
        window.addEventListener("scroll", syncRail, { passive: true });

        let ro;
        if ("ResizeObserver" in window && leftColRef.current) {
            ro = new ResizeObserver(syncRail);
            ro.observe(leftColRef.current);
        }

        return () => {
            window.removeEventListener("resize", syncRail);
            window.removeEventListener("scroll", syncRail);
            if (ro) ro.disconnect();
        };
    }, []);

    useEffect(() => {
        try {
            const fn = syncRailRef.current;
            if (typeof fn === "function") setTimeout(fn, 0);
        } catch {
            /* ignore */
        }
    }, [showNotifs]);


    const [selectedDtvid, setSelectedDtvid] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetchErr, setFetchErr] = useState("");
    const [notifs, setNotifs] = useState([]);

    // Offboard modal state (ManageRequest parity)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [note, setNote] = useState("");
    const [endDate, setEndDate] = useState("");
    const [offboarding, setOffboarding] = useState(false);
    const [offBoardError, setOffboardError] = useState("");
    const [offBoardServerError, setOffboardServerError] = useState("");

    const lookupInFlightRef = useRef(false);
    const lookupWaitRef = useRef(null);
    useEffect(() => () => {
        if (lookupWaitRef.current) clearTimeout(lookupWaitRef.current);
    }, []);
    const notifInFlightRef = useRef(false);

    const NOTIF_CACHE_KEY = "npw.notifs.v1";
    const LOOKUP_CACHE_KEY = "npw.fte.lookup.v1";
    const NOTIF_TTL_MS = 5 * 60 * 1000;
    const LOOKUP_TTL_MS = 15 * 60 * 1000;

    /* Minimum time between activation refreshes (route changes), default 5 minutes */
    const MIN_ACTIVATE_REFRESH_MS = Number(ENV.MIN_ACTIVATE_REFRESH_MS || 300000);
    const lastActivateRef = useRef({ lookupAt: 0, notifAt: 0 });

    // Derived: whether Others is allowed (disable if no rows)
    const hasOthers = Array.isArray(allRows) && allRows.length > 0;

    /* ================== CENTRAL FETCHERS ================== */
    const fetchNotifs = async ({ force = false } = {}) => {
        if (notifInFlightRef.current) return;
        notifInFlightRef.current = true;
        try {
            const cached = sessionStorage.getItem(NOTIF_CACHE_KEY);
            if (!force && cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (parsed?.at && Array.isArray(parsed.data) && Date.now() - parsed.at < NOTIF_TTL_MS) {
                        setNotifs(parsed.data);
                        return;
                    }
                } catch { }
            }

            const url = buildNotifUrl();
            if (!url) {
                setNotifs(NOTIF_FALLBACK);
                return;
            }
            const data = await tbGetJson(url);
            const list = Array.isArray(data) && data.length ? data.slice(0, 5) : NOTIF_FALLBACK;
            setNotifs(list);
            sessionStorage.setItem(NOTIF_CACHE_KEY, JSON.stringify({ at: Date.now(), data: list }));
        } catch {
            setNotifs(NOTIF_FALLBACK);
        } finally {
            notifInFlightRef.current = false;
        }
    };

    const fetchLookup = async ({ force = false } = {}) => {
        if (lookupInFlightRef.current) return;
        // Wait for the lookup API to finish loading before firing FTE hinge/npw APIs.
        // Applies to every role — no role checks. Re-runs automatically once lookup is ready.
        if (!isLookupReady()) {
            if (!lookupWaitRef.current) {
                lookupWaitRef.current = setTimeout(() => {
                    lookupWaitRef.current = null;
                    fetchLookup({ force });
                }, 200);
            }
            return;
        }
        lookupInFlightRef.current = true;
        setFetchErr("");
        setLoading(true);

        try {
            const cached = sessionStorage.getItem(LOOKUP_CACHE_KEY);

            // If cache contains placeholder dashes, ignore it and refetch.
            if (!force && cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (parsed?.at && parsed?.data && Date.now() - parsed.at < LOOKUP_TTL_MS) {
                        const { self, mapped } = parsed.data;
                        if (isPlaceholderSelf(self)) {
                            try { sessionStorage.removeItem(LOOKUP_CACHE_KEY); } catch { }
                        } else {
                            setPersonInfo(self);
                            console.log(self);
                            setAllRows(mapped);
                            setTotal(mapped.length);

                            setSelectedDtvid(mapped[0]?.dtvid || "");
                            primeSubmitContext({ mode: "self", self, selectedDtvid: mapped[0]?.dtvid || "" });
                            return;
                        }
                    }
                } catch { }
            }

            const url = buildLookupUrl();
            if (!url) {
                throw new Error(
                    "Lookup is not configured or session is missing an identifier. Configure LOOKUP_URL/TB_HINGE_PERSON_LOOKUP_URL/TB_HINGE_LOOKUP_URL/TB_LOOKUP_URL and ensure tb_dtvid or tb_email is in session."
                );
            }

            // ✅ call TB, not Hinge
            const payload = await tbGetJson(url);

            const inner = unwrapLookup(payload);
            if (!inner) throw new Error("Lookup returned no data.");

            // ✅ ONLY map attributes needed for the Overview FTE UI.
            const self = {
                dtvAnchorId: inner?.dtvAnchorId || inner?.dtvId || inner?.dtvid || "-", // used for actions
                firstName: inner?.firstName || "-",
                lastName: inner?.lastName || "-",
                primaryEmail: inner?.primaryEmail || inner?.email || "-",
                attuid: inner?.attuid || inner?.attUid || inner?.attUID || null,
                countryOfCitizenship: inner?.countryOfCitizenship ?? null,
                phone: inner?.phone ?? null,
                employeeSponsor: inner?.employeeSponsor ?? null,
                supervisorDtvAnchorId: inner?.supervisorDtvAnchorId ?? null,
                supervisorAttuid: inner?.supervisorAttuid ?? null,
            };

            if (isPlaceholderSelf(self)) {
                throw new Error("Lookup returned an empty profile (missing dtvAnchorId/primaryEmail). Check the lookup endpoint response shape and session identifiers.");
            }

            const directReports = Array.isArray(inner?.directReports) ? inner.directReports : [];
            const mapped = directReports.map(mapDirectReport);

            setPersonInfo(self);
            setAllRows(mapped);
            setTotal(mapped.length);

            const firstPick = mapped[0]?.dtvid || "";
            setSelectedDtvid(firstPick);

            sessionStorage.setItem(LOOKUP_CACHE_KEY, JSON.stringify({ at: Date.now(), data: { self, mapped } }));
            primeSubmitContext({ mode: "self", self, selectedDtvid: firstPick });
        } catch (e) {
            setPersonInfo(null);
            setAllRows([]);
            setTotal(0);
            setFetchErr(typeof e?.message === "string" ? e.message : "Load failed");
        } finally {
            setLoading(false);
            lookupInFlightRef.current = false;
        }
    };

    /* ======================= INITIAL LOAD ======================= */
    useEffect(() => {
        fetchNotifs({ force: true });
        fetchLookup({ force: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Fire on route changes / bfcache only, respect TTL + min interval */
    usePageActivation(() => {
        const now = Date.now();
        if (now - lastActivateRef.current.notifAt >= MIN_ACTIVATE_REFRESH_MS) {
            lastActivateRef.current.notifAt = now;
            fetchNotifs({ force: false });
        }
        if (now - lastActivateRef.current.lookupAt >= MIN_ACTIVATE_REFRESH_MS) {
            lastActivateRef.current.lookupAt = now;
            fetchLookup({ force: false });
        }
    }, 200);

    /* UI state */
    useEffect(() => {
        setPage(1);
    }, [mode]);

    // Enforce: if Others has no data, force mode to self and keep it.
    useEffect(() => {
        if (!hasOthers && mode === "others") setMode("self");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOthers]);

    /* ======================
       DISPLAY FIELDS (MINIMAL)
       ====================== */
    const displayFirst = personInfo?.firstName ?? "-";
    const displayLast = personInfo?.lastName ?? "-";
    const displayEmail = personInfo?.primaryEmail ?? "-";

    const displayCountry = clean(personInfo?.countryOfCitizenship) || "-";
    const displayPhone = clean(personInfo?.phone) || "-";

    const displaySponsorMgr =
        clean(personInfo?.employeeSponsor) ||
        clean(personInfo?.supervisorDtvAnchorId) ||
        clean(personInfo?.supervisorAttuid) ||
        "-";

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const GRID_LINE = "#e5e7eb";
    const cellPad = { padding: "6px 8px", fontSize: 12, lineHeight: 1.15 };
    const tdTrunc = { ...cellPad, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
    const thBase = { ...cellPad, background: "#fff", fontWeight: 600, borderBottom: `1px solid ${GRID_LINE}` };

    const rows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return allRows.slice(start, start + pageSize);
    }, [allRows, page, pageSize]);

    /* ===== Offboarding eligibility (ATTUID-only) ===== */
    // A direct report cannot be offboarded when its ATTUID is deactivated
    // (deactivatedAttuid === "y") and it still has an attuid value.
    const isReporteeOffboardBlocked = (row) => {
        const rowAttuid = clean(row?.attuid);
        const rowDeactivated = clean(row?.deactivatedAttuid).toLowerCase() === "y";
        return !!rowAttuid && rowDeactivated;
    };

    const selectedOffboardAttuid = useMemo(() => {
        if (mode === "self") return clean(personInfo?.attuid);
        const row = allRows.find((x) => String(x?.dtvid || "") === String(selectedDtvid));
        return clean(row?.attuid);
    }, [mode, personInfo, allRows, selectedDtvid]);

    const selectedReporteeBlocked = useMemo(() => {
        if (mode !== "others") return false;
        const row = allRows.find((x) => String(x?.dtvid || "") === String(selectedDtvid));
        if (!row) return false;
        return isReporteeOffboardBlocked(row);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, allRows, selectedDtvid]);

    const canOffboard = !!selectedOffboardAttuid && !selectedReporteeBlocked;

    const offboardWorkerName = useMemo(() => {
        if (mode === "self") return `${personInfo?.firstName || ""} ${personInfo?.lastName || ""}`.trim();
        const row = allRows.find((x) => String(x?.dtvid || "") === String(selectedDtvid));
        return clean(row?.full_name) || `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
    }, [mode, personInfo, allRows, selectedDtvid]);


    /* ===== Onboarding (button functionality) ===== */
    const goOnboarding = (dtvid) => {
        const from = `${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}` || "/";
        navigate(`/onboarding/new?dtvid=${encodeURIComponent(String(dtvid))}`, { state: { from } });
    };

    const handleSelfOnboard = () => {
        const anchor = personInfo?.dtvAnchorId || getDtvId() || ENV.DEFAULT_DTVID;
        if (!anchor) return alert("Missing DTVID (anchor id).");
        primeSubmitContext({ mode: "self", self: personInfo, selectedDtvid: anchor });
        try {
            sessionStorage.setItem("tb_selected_dtvid", String(anchor));
        } catch { }
        goOnboarding(anchor);
    };

    const handleOthersOnboard = () => {
        if (!hasOthers) return alert("No direct reports found.");
        if (!selectedDtvid) return alert("Please select a worker.");
        primeSubmitContext({ mode: "others", self: personInfo, selectedDtvid });
        try {
            sessionStorage.setItem("tb_selected_dtvid", String(selectedDtvid));
        } catch { }
        goOnboarding(selectedDtvid);
    };

    /* ===== Offboard (open ManageRequest-like modal; same endpoint/payload) ===== */
    const openOffboardModal = () => {
        if (!canOffboard) {
            toast.error("Offboarding is disabled because ATTUID is missing for this person.");
            return;
        }
        setOffboardError("");
        setOffboardServerError("");
        setNote("");
        setEndDate("");
        setIsModalOpen(true);
    };

    const submitOffboard = async () => {
        const today = new Date().toISOString().split("T")[0];

        const dtvIdToOffboard =
            mode === "self"
                ? (personInfo?.dtvAnchorId || getDtvId() || ENV.DEFAULT_DTVID || "")
                : (selectedDtvid || "");


        const attuidToOffboard =
            mode === "self"
                ? clean(personInfo?.attuid)
                : clean((allRows.find((x) => String(x?.dtvid || "") === String(dtvIdToOffboard)) || {})?.attuid);

        if (!dtvIdToOffboard) {
            setOffboardError(mode === "self" ? "Missing DTVID (anchor id)." : "Please select a worker.");
            return;
        }
        if (!attuidToOffboard) {
            setOffboardError("Missing ATTUID for this person. Offboarding is disabled.");
            return;
        }
        if (mode === "others" && selectedReporteeBlocked) {
            setOffboardError("Offboarding is disabled because the FTE ATTUID is deactivated.");
            return;
        }
        if (mode === "others" && !hasOthers) {
            setOffboardError("No direct reports found.");
            return;
        }
        if (!endDate) {
            setOffboardError("Please select an end date.");
            return;
        }
        if (new Date(endDate) < new Date(today)) {
            setOffboardError("Please select an end date greater than or equal to today.");
            return;
        }
        setOffboardError("");

        const row = mode === "others" ? allRows.find((x) => String(x?.dtvid || "") === String(dtvIdToOffboard)) : null;

        const name =
            mode === "self"
                ? `${personInfo?.firstName || ""} ${personInfo?.lastName || ""}`.trim()
                : `${row?.first_name || ""} ${row?.last_name || ""}`.trim();

        const actor = getActorDtvId();
        const actorName = getActorName();
        const actorEmail = getActorEmail();
        const performedAt = new Date().toISOString();
        const role = getStoredRole();

        if (!OFFBOARD_URL) {
            setOffboardServerError("OFFBOARD_URL is not configured in ENV.");
            toast.error("OFFBOARD_URL is not configured in ENV.");
            return;
        }

        const payload = {
            end_date: endDate, // YYYY-MM-DD
            comments: note ?? "",
            performed_by_dtv_id: actor || "",
            performedName: actorName || actor || "",
            actorEmail: actorEmail || "",

            submitter_id: (actorEmail || undefined),
            submitter: (actorEmail || undefined),
            submitter_email: (actorEmail || undefined),
            submitter_name: (actorName || actor || undefined),
            submitterDtvid: (actor || undefined),
            submitter_dtvid: (actor || undefined),
            rawActorEmail: (actorEmail || undefined),
            performed_at: performedAt, // ISO
            name: name || "",
            action: "offboard",

            // User requirement: send the selected DTVID as dtv_id
            dtv_id: String(dtvIdToOffboard),

            // Also send ATTUID explicitly for ATTUID-based offboarding
            attuid: String(attuidToOffboard),
            offboard_attuid: String(attuidToOffboard),

            role: role || "",
            performed_by_role: role || "",
            offboardIdType: "ATTUID",
            offboardByDtvId: false,
            offboardByAttuid: true,
        };

        console.log("[FTE] Offboard PUT", { url: OFFBOARD_URL, payload });

        try {
            setOffboardServerError("");
            setOffboarding(true);

            await tbPutJson(OFFBOARD_URL, payload);

            setIsModalOpen(false);
            toast.success("Off board request submitted");
        } catch (e) {
            console.error("Offboard failed:", e);
            const msg = extractOffboardError(e);
            setOffboardServerError(msg);
            toast.error(msg);
        } finally {
            setOffboarding(false);
        }
    };

    function onNotifClick(n, idx) {
        const target = notifTargetPath(n, idx);
        if (!target) return;
        navigate(target);
    }

    /* =========================
       Notifications count badge
       ========================= */
    const notifCount = loading ? null : Math.min(5, Array.isArray(notifs) ? notifs.length : 0);

    const bellIconStyle = {
        fontSize: 18,
        lineHeight: 1,
        display: "block",
    };

    const badgeOnBellStyle = {
        position: "absolute",
        top: -4,
        right: -4,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 16,
        height: 16,
        padding: "0 5px",
        borderRadius: 999,
        background: "#f87171",
        color: "#fff",
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: "0 0 0 2px #fff",
        pointerEvents: "none",
    };

    return (
        <AppShell activeKey="overview">
            <ToastContainer />
            <div className="dash-grid" style={{ gridTemplateColumns: showNotifs ? "minmax(0,1fr) 260px" : "minmax(0,1fr)" }}>
                {/* LEFT column */}
                <div className="left-col" ref={leftColRef} style={{ paddingTop: 6 }}>
                    <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 800, marginBottom: 6 }}>
                        Personal Details:
                    </div>

                    {/* ✅ ONLY these attributes (per 2nd screenshot) */}
                    <div className="ovr-fte-grid" style={{ marginBottom: 8, maxWidth: 720 }}>
                        <DetailRow label="First Name" value={displayFirst} />
                        <DetailRow label="Last Name" value={displayLast} />

                        <DetailRow label="Email" value={displayEmail} />
                        <DetailRow label="Phone" value={displayPhone} />

                        <DetailRow label="Country Of Citizenship" value={displayCountry} />
                        <DetailRow label="Sponsor/ Manager" value={displaySponsorMgr} />
                    </div>

                    {!!fetchErr && (
                        <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6, marginBottom: 6 }}>
                            {fetchErr}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="ovr-fte-actions" style={{ marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                            className="btn-navy"
                            onClick={mode === "self" ? handleSelfOnboard : handleOthersOnboard}
                            style={{ minWidth: 120 }}
                        >
                            Onboard
                        </button>

                        <button
                            className="btn-navy"
                            onClick={openOffboardModal}
                            style={{ minWidth: 120 }}
                            title={canOffboard ? "Start offboarding" : (selectedReporteeBlocked ? "Offboarding is disabled: FTE ATTUID is deactivated." : "Offboarding is disabled: ATTUID is missing for this person.")}
                            disabled={!canOffboard}
                        >
                            Offboard
                        </button>

                        <div style={{ flex: 1 }} />

                        {!showNotifs && (
                            <button
                                type="button"
                                onClick={() => setShowNotifs((v) => !v)}
                                style={{
                                    position: "relative",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 40,
                                    height: 40,
                                    borderRadius: 999,
                                    border: "1px solid #e5e7eb",
                                    background: "#fff",
                                    cursor: "pointer",
                                }}
                                aria-label={showNotifs ? "Hide notifications" : "Show notifications"}
                                title={showNotifs ? "Hide notifications" : "Show notifications"}
                            >
                                <span className="bell-ico" style={bellIconStyle} aria-hidden="true">🔔</span>
                                {!loading && (notifCount ?? 0) > 0 && (
                                    <span style={badgeOnBellStyle} aria-label={`Notifications count ${notifCount}`} title={`${notifCount}`}>
                                        {notifCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>

                    {/* Radio toggles */}
                    <div className="radio-inline" style={{ marginTop: 6, marginBottom: 0 }}>
                        <label>
                            <input type="radio" name="so" checked={mode === "self"} onChange={() => setMode("self")} />
                            &nbsp;Self
                        </label>

                        <label
                            style={{ opacity: hasOthers ? 1 : 0.45, cursor: hasOthers ? "pointer" : "not-allowed" }}
                            title={hasOthers ? "" : "No direct reports available"}
                        >
                            <input
                                type="radio"
                                name="so"
                                checked={mode === "others"}
                                onChange={() => {
                                    if (hasOthers) setMode("others");
                                }}
                                disabled={!hasOthers}
                            />
                            &nbsp;Others
                        </label>
                    </div>

                    {/* Others table */}
                    {mode === "others" && hasOthers && (
                        <div className="table-card" style={{ marginTop: 0, paddingBottom: "calc(var(--footer-h) + 90px)" }}>
                            <div className="table-title" style={{ marginBottom: 6 }}>
                                Tagged Employees
                            </div>
                            <div className="table-wrap">
                                <table>
                                    <colgroup>
                                        <col style={{ width: "8%" }} />
                                        <col style={{ width: "13%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "20%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "13%" }} />
                                        <col style={{ width: "13%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={thBase}>Select</th>
                                            <th style={thBase}>DTVID</th>
                                            <th style={thBase}>ATTUID</th>
                                            <th style={thBase}>Worker Name</th>
                                            <th style={thBase}>Role</th>
                                            <th style={thBase}>Status</th>
                                            <th style={thBase}>Start Date</th>
                                            <th style={thBase}>End Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(loading ? Array.from({ length: pageSize }) : rows).map((r, i) => {
                                            if (loading) {
                                                return (
                                                    <tr
                                                        key={`sk-${i}`}
                                                        style={{
                                                            background: i % 2 ? "#fffef6" : "#fff",
                                                            borderBottom: `1px solid ${GRID_LINE}`,
                                                        }}
                                                    >
                                                        {Array.from({ length: 8 }).map((_, j) => (
                                                            <td key={j} style={{ ...tdTrunc, borderBottom: `1px solid ${GRID_LINE}` }}>
                                                                <span className="skeleton-line w-60" />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            }

                                            const nm = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || "-";
                                            const attuidBlocked = isReporteeOffboardBlocked(r);
                                            return (
                                                <tr
                                                    key={r.dtvid || i}
                                                    style={{
                                                        background: i % 2 ? "#fffef6" : "#fff",
                                                        borderBottom: `1px solid ${GRID_LINE}`,
                                                    }}
                                                    onClick={() => {
                                                        setSelectedDtvid(r.dtvid);
                                                        primeSubmitContext({ mode: "others", self: personInfo, selectedDtvid: r.dtvid });
                                                    }}
                                                    role="button"
                                                    title={r.email ? `Email: ${r.email}` : "Click to select this worker"}
                                                >
                                                    <td style={tdTrunc} title="Select">
                                                        <input
                                                            type="radio"
                                                            name="pick"
                                                            checked={selectedDtvid === r.dtvid}
                                                            onChange={() => {
                                                                setSelectedDtvid(r.dtvid);
                                                                primeSubmitContext({ mode: "others", self: personInfo, selectedDtvid: r.dtvid });
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={tdTrunc} title={String(r.dtvid)}>
                                                        <span className="sec-pill sec-pill-link" style={{ cursor: "default" }} title={String(r.dtvid)}>
                                                            {r.dtvid}
                                                        </span>
                                                    </td>
                                                    <td
                                                        style={{ ...tdTrunc, ...(attuidBlocked ? { color: "#b91c1c", fontWeight: 600 } : null) }}
                                                        title={attuidBlocked ? "Offboarding is disabled: FTE ATTUID is deactivated." : String(r.attuid || "-")}
                                                    >
                                                        {r.attuid || "-"}
                                                    </td>
                                                    <td style={tdTrunc} title={nm}>
                                                        <span className="sec-pill sec-pill-link" style={{ cursor: "default" }} title={nm}>
                                                            {nm}
                                                        </span>
                                                    </td>
                                                    <td style={tdTrunc} title={String(r.role || "-")}>{r.role || "-"}</td>
                                                    <td style={tdTrunc} title={String(r.status || "-")}>
                                                        <span className={`status-pill ${String(r.status || "").toLowerCase().includes("info") ? "info" : ""}`}>
                                                            {r.status || "-"}
                                                        </span>
                                                    </td>
                                                    <td style={tdTrunc} title={String(r.start_date || "-")}>{r.start_date || "-"}</td>
                                                    <td style={tdTrunc} title={String(r.end_date || "-")}>{r.end_date || "-"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {!!fetchErr && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>{fetchErr}</div>}
                        </div>

                    )}
                    {/* Pager (fixed rail, aligned to LEFT column) */}
                    {mode === "others" && hasOthers && total > 0 && (
                        <div
                            className="pager-rail-fixed"
                            style={{
                                position: "fixed",
                                left: railBox.left,
                                width: railBox.width,
                                maxWidth: "100vw",
                                boxSizing: "border-box",
                                bottom: "calc(var(--footer-h) + 28px)",
                                zIndex: 40,
                            }}
                            aria-live="polite"
                        >
                            <div
                                className="pager-rail-inner"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    flexWrap: "wrap",
                                    width: "100%",
                                    boxSizing: "border-box",
                                }}
                            >
                                <span className="pg-status" style={{ flex: "1 1 auto", minWidth: 140 }}>
                                    Page {page} of {totalPages}
                                </span>

                                <div className="pager-buttons" style={{ display: "flex", gap: 10, flexWrap: "nowrap" }}>
                                    <label className="pg-rowsize" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 12, color: "#64748b" }}>Rows:</span>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => {
                                                const v = Number(e.target.value || 5);
                                                setPageSize(v);
                                                setPage(1);
                                            }}
                                            style={{
                                                height: 34,
                                                borderRadius: 10,
                                                border: "1px solid #e5e7eb",
                                                padding: "0 10px",
                                                background: "#fff",
                                                fontSize: 13,
                                            }}
                                        >
                                            {[5, 10, 25, 50].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <button
                                        type="button"
                                        className="pg"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1 || loading}
                                    >
                                        ◀ Previous
                                    </button>
                                    <button
                                        type="button"
                                        className="pg"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages || loading}
                                    >
                                        Next ▶
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {mode === "others" && !hasOthers && (
                        <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12 }}>No direct reports found.</div>
                    )}
                </div>

                {/* RIGHT column — Notifications */}
                {showNotifs && (
                    <div className="right-col">
                        <div className="panel notif-panel">
                            <div className="panel-hdr" style={{ display: "flex", alignItems: "center", gap: 10 }}>

                                <button
                                    type="button"
                                    className="notif-bell-toggle"
                                    onClick={() => setShowNotifs((v) => !v)}
                                    aria-label={showNotifs ? "Hide notifications" : "Show notifications"}
                                    title={showNotifs ? "Hide notifications" : "Show notifications"}
                                >
                                    <span className="bell-ico" style={bellIconStyle} aria-hidden="true">🔔</span>
                                    {!loading && (notifCount ?? 0) > 0 && (
                                        <span style={badgeOnBellStyle} aria-label={`Notifications count ${notifCount}`} title={`${notifCount}`}>
                                            {notifCount}
                                        </span>
                                    )}
                                </button>
                                <span className="notif-h">Notifications</span>
                                <button
                                    type="button"
                                    onClick={() => setShowNotifs(false)}
                                    style={{
                                        marginLeft: "auto",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        border: "1px solid #e5e7eb",
                                        background: "#fff",
                                        cursor: "pointer",
                                    }}
                                    aria-label="Close notifications"
                                    title="Close notifications"
                                >
                                    ✕
                                </button>
                            </div>

                            <ul className="notif-list">
                                {notifs.map((n, idx) => {
                                    const title = formatNotifTitle(n);
                                    const href = notifTargetPath(n, idx);
                                    return (
                                        <li key={n.id ?? idx}>
                                            <div className="dot" />
                                            <div>
                                                <div className="n-title">
                                                    <a
                                                        href={href}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            onNotifClick(n, idx);
                                                        }}
                                                        title={title}
                                                    >
                                                        {title}
                                                    </a>
                                                </div>
                                                <div className="n-ago">{n.ago}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* Offboard modal — same markup/classes as ManageRequest */}
            {isModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="offboard-title">
                    <div className="modal">
                        <h2 id="offboard-title">Off Board ATTUID of Worker</h2>
                        <p>Are you sure you want to offboard this worker?</p>

                        <div className="field">
                            <label htmlFor="offboard-worker-name">Worker Name</label>
                            <input id="offboard-worker-name" type="text" value={offboardWorkerName || ""} disabled />
                        </div>

                        <div className="field">
                            <label htmlFor="offboard-date">End Date</label>
                            <input
                                id="offboard-date"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                min={new Date().toISOString().split("T")[0]}
                            />
                            {offBoardError && <div className="err">{offBoardError}</div>}
                        </div>

                        <div className="field">
                            <label htmlFor="offboard-notes">Notes (optional)</label>
                            <textarea
                                id="offboard-notes"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                placeholder="Add any context for the offboard request"
                            />
                        </div>

                        {offBoardServerError && <div className="err">{offBoardServerError}</div>}

                        <div className="modal-actions">
                            <button className="btn btn-ghost" onClick={() => setIsModalOpen(false)} disabled={offboarding}>
                                Cancel
                            </button>
                            <button className="btn btn-cta" onClick={submitOffboard} disabled={offboarding}>
                                {offboarding ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}

function DetailRow({ label, value }) {
    return (
        <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
            <div style={{ color: "#334155", fontSize: 12.5, whiteSpace: "nowrap" }}>{label} :</div>
            <div
                title={String(value ?? "")}
                style={{
                    fontWeight: 600,
                    fontSize: 13,
                    minWidth: 0,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {value || "-"}
            </div>
        </div>
    );
}